import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import SEO from "../components/SEO";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-white font-['Manrope']" data-testid="privacy-page">
      <SEO
        page="privacy"
        customTitle="Politique de Confidentialité | HostProtect"
        customDescription="Politique de confidentialité de HostProtect. Découvrez comment nous protégeons vos données personnelles conformément au RGPD."
      />

      {/* Header */}
      <header className="backdrop-blur-xl bg-white/90 border-b border-neutral-200/50 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 md:px-12">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto" />
            </Link>
            <Link
              to="/"
              className="flex items-center gap-2 text-sm text-neutral-600 hover:text-[#19294C] transition-colors font-medium"
              data-testid="privacy-back-btn"
            >
              <ArrowLeft className="w-4 h-4" />
              Retour
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="bg-gradient-to-b from-[#19294C] to-[#1c2f55] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <motion.h1
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white font-['Outfit']"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6 }}
          >
            Politique de Confidentialité
          </motion.h1>
          <motion.p
            className="mt-4 text-white/60 text-sm"
            variants={fadeUp}
            initial="hidden"
            animate="visible"
            transition={{ duration: 0.6, delay: 0.15 }}
          >
            Dernière mise à jour : 30 avril 2025
          </motion.p>
        </div>
      </section>

      {/* Content */}
      <motion.main
        className="max-w-4xl mx-auto px-6 py-12 md:py-16"
        variants={fadeUp}
        initial="hidden"
        animate="visible"
        transition={{ duration: 0.5, delay: 0.2 }}
      >
        <div className="prose prose-neutral max-w-none prose-headings:font-['Outfit'] prose-headings:text-[#19294C] prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h3:text-lg prose-h3:mt-8 prose-h3:mb-3 prose-p:text-neutral-600 prose-p:leading-relaxed prose-li:text-neutral-600 prose-strong:text-[#19294C]">

          <h2>1. Responsable du traitement</h2>
          <p>
            Les données personnelles collectées sur le site <a href="https://www.hostprotect.io" className="text-[#00B8E4] hover:underline" target="_blank" rel="noopener noreferrer">www.hostprotect.io</a> sont traitées par :
          </p>
          <p>PM Hospitality, SAS au capital de 100 €, immatriculée au RCS d'Épinal sous le n° 938 069 671</p>
          <p>Siège social : 27 rue de la Gare, 88380 Arches (France)</p>
          <p>
            Email : <a href="mailto:support@hostprotect.io" className="text-[#00B8E4] hover:underline">support@hostprotect.io</a>
          </p>

          <h2>2. Données collectées</h2>
          <p>HostProtect collecte et traite notamment les catégories de données suivantes :</p>
          <ul>
            <li>Informations d'identité : nom, prénom</li>
            <li>Coordonnées : email, téléphone</li>
            <li>Adresse postale du logement assuré</li>
            <li>Informations contractuelles : date de souscription, historique</li>
            <li>Informations de paiement (via Stripe, non stockées)</li>
            <li>Données de navigation (cookies, adresse IP, type d'appareil)</li>
            <li>Informations transmises lors de la déclaration de sinistre</li>
          </ul>

          <h2>3. Finalités des traitements</h2>
          <p>Les données sont collectées pour les finalités suivantes :</p>
          <ul>
            <li>Création et gestion de votre compte client</li>
            <li>Souscription à l'assurance et gestion du contrat</li>
            <li>Accès à l'espace personnel et aux documents (attestation, historique, factures)</li>
            <li>Transmission des données à nos partenaires (MEETCH, Mutuaide Assistance) pour gestion du contrat</li>
            <li>Traitement des sinistres et réclamations</li>
            <li>Analyse du trafic et performances du site (Google Analytics)</li>
            <li>Publicité ciblée (Meta Pixel)</li>
            <li>Respect des obligations légales et réglementaires</li>
          </ul>

          <h2>4. Base légale du traitement</h2>
          <p>Le traitement des données repose sur :</p>
          <ul>
            <li>L'exécution d'un contrat (assurance, espace client)</li>
            <li>Le respect des obligations légales</li>
            <li>Le consentement de l'utilisateur (cookies marketing)</li>
            <li>L'intérêt légitime de PM Hospitality à améliorer ses services</li>
          </ul>

          <h2>5. Durée de conservation</h2>
          <p>Les données personnelles sont conservées :</p>
          <ul>
            <li>Pendant toute la durée du contrat + 5 ans à des fins de preuve</li>
            <li>Les cookies de mesure d'audience et traceurs publicitaires : 13 mois maximum</li>
          </ul>

          <h2>6. Destinataires des données</h2>
          <p>Les données peuvent être transmises à :</p>
          <ul>
            <li>MEETCH, en tant que courtier gestionnaire mandaté par l'assureur Mutuaide Assistance</li>
            <li>Mutuaide Assistance, assureur agréé en charge de l'indemnisation</li>
            <li>Stripe, prestataire de paiement</li>
            <li>AppUp, prestataire technique de l'infrastructure</li>
            <li>Sous-traitants autorisés (prestataires hébergeurs, maintenance)</li>
          </ul>
          <p>
            Tous les tiers accèdent aux données uniquement dans le cadre strict de leur mission, en qualité de sous-traitants ou co-responsables de traitement.
          </p>

          <h2>7. Transferts hors Union européenne</h2>
          <p>
            Certains prestataires peuvent effectuer des transferts de données hors UE (Meta, Google, Stripe). Ces transferts sont encadrés par :
          </p>
          <ul>
            <li>Des clauses contractuelles types validées par la Commission européenne</li>
            <li>Des garanties techniques et organisationnelles conformes au RGPD</li>
          </ul>

          <h2>8. Vos droits</h2>
          <p>Conformément aux articles 15 à 22 du RGPD, vous disposez des droits suivants :</p>
          <ul>
            <li>Droit d'accès à vos données</li>
            <li>Droit de rectification</li>
            <li>Droit à l'effacement ("droit à l'oubli")</li>
            <li>Droit à la portabilité</li>
            <li>Droit d'opposition au traitement</li>
            <li>Droit à la limitation du traitement</li>
          </ul>
          <p>
            Vous pouvez exercer ces droits à tout moment en contactant : <a href="mailto:support@hostprotect.io" className="text-[#00B8E4] hover:underline">support@hostprotect.io</a>
          </p>
          <p>
            En cas de demande de suppression, nous procéderons à la fermeture du compte et à l'effacement des données non soumises à une obligation légale de conservation.
          </p>
          <p>
            Vous avez également le droit d'introduire une réclamation auprès de la CNIL : <a href="https://www.cnil.fr" className="text-[#00B8E4] hover:underline" target="_blank" rel="noopener noreferrer">www.cnil.fr</a>
          </p>

          <h2>9. Cookies et traceurs</h2>
          <p>
            Un bandeau de consentement s'affiche lors de votre première visite pour recueillir votre accord sur les cookies utilisés. Vous pouvez modifier vos choix à tout moment via l'interface de gestion des cookies.
          </p>
          <p>Les cookies utilisés peuvent avoir les finalités suivantes :</p>
          <ul>
            <li>Cookies strictement nécessaires au fonctionnement du site</li>
            <li>Cookies de mesure d'audience (Google Analytics)</li>
            <li>Cookies de publicité et retargeting (Meta Pixel)</li>
          </ul>

          <h2>10. Modification de la présente politique</h2>
          <p>
            HostProtect se réserve le droit de modifier la présente politique à tout moment. Toute nouvelle version sera publiée sur cette page avec la date de mise à jour.
          </p>

          <h2>11. Contact</h2>
          <p>
            Pour toute question sur cette politique ou sur vos données personnelles : <a href="mailto:support@hostprotect.io" className="text-[#00B8E4] hover:underline">support@hostprotect.io</a>
          </p>

        </div>
      </motion.main>

      {/* Footer */}
      <footer className="bg-[#19294C] text-white py-12 px-6 md:px-12" data-testid="privacy-footer">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <Link to="/" className="flex items-center">
              <img src="/logo-hostprotect.png" alt="HostProtect" className="h-9 w-auto brightness-0 invert" />
            </Link>
            <div className="flex items-center gap-6 text-sm text-white/60">
              <Link to="/conditions-generales-de-vente" className="hover:text-white transition-colors" data-testid="privacy-link-cgv">
                Conditions générales de vente
              </Link>
              <Link to="/connexion" className="hover:text-white transition-colors">
                Espace client
              </Link>
            </div>
          </div>
          <div className="mt-8 pt-6 border-t border-white/10 text-center">
            <p className="text-white/40 text-sm">© {new Date().getFullYear()} HostProtect — PM Hospitality SAS. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
